import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import cn from './cn.js';
import Spinner from './Spinner.js';

const base =
  'inline-flex items-center justify-center gap-x-2 whitespace-nowrap rounded-lg font-medium select-none transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

export const buttonVariants = {
  primary: 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-500 active:bg-indigo-700',
  secondary: 'bg-white text-gray-900 ring-1 ring-inset ring-gray-200 shadow-sm hover:bg-gray-50 active:bg-gray-100',
  soft: 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 active:bg-indigo-200',
  ghost: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200',
  danger: 'bg-red-600 text-white shadow-sm hover:bg-red-500 active:bg-red-700',
  dangerSoft: 'bg-red-50 text-red-700 hover:bg-red-100 active:bg-red-200',
  dangerGhost: 'text-red-600 hover:bg-red-50 active:bg-red-100',
  success: 'bg-emerald-600 text-white shadow-sm hover:bg-emerald-500 active:bg-emerald-700',
  inverted: 'bg-white/10 text-white ring-1 ring-inset ring-white/20 hover:bg-white/15 active:bg-white/20',
  link: 'h-auto px-0 text-indigo-600 underline-offset-4 hover:text-indigo-500 hover:underline',
};

export const buttonSizes = {
  xs: 'h-7 px-2.5 text-xs',
  sm: 'h-8 px-3 text-sm',
  md: 'h-9 px-3.5 text-sm',
  lg: 'h-10 px-4 text-sm',
  xl: 'h-11 px-5 text-base',
};

const iconSizes = {
  xs: 'h-3.5 w-3.5',
  sm: 'h-4 w-4',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
  xl: 'h-5 w-5',
};

/**
 * Button
 * - `variant`: primary | secondary | soft | ghost | danger | dangerSoft | dangerGhost | success | inverted | link
 * - `size`: xs | sm | md | lg | xl
 * - `icon` / `trailingIcon`: icon components (heroicons / lucide)
 * - `loading`: shows a spinner in place of the leading icon and disables the button
 * - `to`: renders a react-router Link (client side navigation, no full reload)
 * - `href`: renders an anchor (external links)
 */
const Button = forwardRef(function Button(
  {
    as,
    to,
    href,
    variant = 'primary',
    size = 'md',
    loading = false,
    icon: Icon,
    trailingIcon: TrailingIcon,
    block = false,
    className,
    children,
    disabled,
    type,
    ...props
  },
  ref,
) {
  const classes = cn(
    base,
    buttonVariants[variant] || buttonVariants.primary,
    variant === 'link' ? '' : buttonSizes[size] || buttonSizes.md,
    block && 'w-full',
    className,
  );

  const iconClass = cn(iconSizes[size] || iconSizes.md, 'shrink-0');

  const content = (
    <>
      {loading ? (
        <Spinner className={iconClass} />
      ) : Icon ? (
        <Icon className={iconClass} aria-hidden="true" />
      ) : null}
      {children}
      {TrailingIcon ? <TrailingIcon className={iconClass} aria-hidden="true" /> : null}
    </>
  );

  if (to) {
    return (
      <Link ref={ref} to={to} className={classes} aria-disabled={disabled || loading || undefined} {...props}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a ref={ref} href={href} className={classes} {...props}>
        {content}
      </a>
    );
  }

  const Comp = as || 'button';

  return (
    <Comp ref={ref} type={type || 'button'} className={classes} disabled={disabled || loading} {...props}>
      {content}
    </Comp>
  );
});

export default Button;
